/**
 * ContactPage — page publique /contact
 * Formulaire de contact avec captcha mathématique.
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, Mail, ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitContactForm } from "@/lib/admin-api";

const TYPE_OPTIONS = [
  { value: "contact", label: "Contact général" },
  { value: "demo_request", label: "Demande de démo" },
  { value: "bug_report", label: "Signaler un bug" },
  { value: "partnership", label: "Partenariat" },
  { value: "other", label: "Autre" },
];

function makeCaptcha() {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return { a, b, expected: a + b };
}

export default function ContactPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    sender_name: "", sender_email: "", sender_organization: "",
    subject: "", message: "", type: "contact", captcha_answer: "",
  });
  const [captcha, setCaptcha] = useState(() => makeCaptcha());
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const disabled = useMemo(
    () => !form.sender_name.trim() || !form.sender_email.trim() ||
          !form.subject.trim() || !form.message.trim() || form.captcha_answer === "",
    [form]
  );

  async function submit(e) {
    e.preventDefault();
    if (disabled) return;
    setSubmitting(true);
    try {
      await submitContactForm({
        sender_name: form.sender_name,
        sender_email: form.sender_email,
        sender_organization: form.sender_organization || null,
        subject: form.subject,
        message: form.message,
        type: form.type,
        captcha_answer: Number(form.captcha_answer),
        captcha_expected: captcha.expected,
      });
      toast.success("Message envoyé ! Nous vous répondrons sous 48 h.");
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Envoi échoué. Réessayez.");
      setCaptcha(makeCaptcha());
      setForm((f) => ({ ...f, captcha_answer: "" }));
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50/30 to-background px-4 py-12 dark:from-red-950/10">
        <div className="mx-auto max-w-xl">
          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/40">
                <Mail className="h-7 w-7 text-emerald-700 dark:text-emerald-400" />
              </div>
              <CardTitle className="text-2xl">Message envoyé</CardTitle>
              <CardDescription>
                Merci pour votre message. Notre équipe vous répondra dans les 48 heures à l'adresse que vous avez fournie.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center gap-2">
              <Button variant="outline" onClick={() => navigate("/")}>Retour à l'accueil</Button>
              <Button onClick={() => { setSent(false); setForm({
                sender_name: "", sender_email: "", sender_organization: "",
                subject: "", message: "", type: "contact", captcha_answer: "",
              }); setCaptcha(makeCaptcha()); }}>Envoyer un autre message</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50/30 to-background px-4 py-12 dark:from-red-950/10" data-testid="public-contact-page">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Retour
        </Link>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Mail className="h-6 w-6 text-red-700 dark:text-red-400" />
              Nous contacter
            </CardTitle>
            <CardDescription>
              Question, démo, partenariat, rapport de bug — nous sommes à l'écoute.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor="name">Nom complet *</Label>
                  <Input id="name" required value={form.sender_name}
                    onChange={(e) => setForm((f) => ({ ...f, sender_name: e.target.value }))}
                    data-testid="contact-name" />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" required value={form.sender_email}
                    onChange={(e) => setForm((f) => ({ ...f, sender_email: e.target.value }))}
                    data-testid="contact-email" />
                </div>
              </div>
              <div>
                <Label htmlFor="org">Organisation</Label>
                <Input id="org" value={form.sender_organization}
                  onChange={(e) => setForm((f) => ({ ...f, sender_organization: e.target.value }))}
                  data-testid="contact-org" />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_180px]">
                <div>
                  <Label htmlFor="subject">Sujet *</Label>
                  <Input id="subject" required value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    data-testid="contact-subject" />
                </div>
                <div>
                  <Label htmlFor="type">Type</Label>
                  <select id="type" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={form.type}
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                    data-testid="contact-type">
                    {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea id="message" rows={6} required value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  data-testid="contact-message" />
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <Label className="text-xs">
                  Anti-spam : combien font <b>{captcha.a} + {captcha.b}</b> ? *
                </Label>
                <Input type="number" required value={form.captcha_answer}
                  onChange={(e) => setForm((f) => ({ ...f, captcha_answer: e.target.value }))}
                  placeholder="Réponse…" className="mt-1 max-w-[160px]"
                  data-testid="contact-captcha" />
              </div>
              <Button type="submit" disabled={disabled || submitting} className="w-full" data-testid="contact-submit">
                <Send className="mr-2 h-4 w-4" />
                {submitting ? "Envoi…" : "Envoyer le message"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
